import AccordianCategoryShapes from '@/views/Products/Components/AccordianCategoryShapes';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Fade } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import PropTypes from 'prop-types';
import React, { useState, memo, useEffect } from 'react';
import parse from 'html-react-parser';

interface CustomTabPanelProps {
    children?: React.ReactNode;
    value: number;
    index: number;
    [key: string]: any;
}

const CustomTabPanel = memo(function CustomTabPanel(props: CustomTabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            {...other}
            sx={{ 
                width: '100%',
                display: value === index ? 'block' : 'none' // Persist all tabs but show only the active one
            }}
        >
            {value === index && (
                <Box sx={{ p: 1 }}>
                    <Fade in={true} timeout={500}>
                        <Typography component={'div'}>{children}</Typography>
                    </Fade>
                </Box>
            )}
        </Box>
    );
});

(CustomTabPanel as any).propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index: any) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

export default function AccordianCategoriesPanel({ tabs }: any) {
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });
   
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
     // Retrieve initial tab from localStorage or URL hash
     const getInitialTab = () => {
        const hash = window.location.hash;
        const match = hash.match(/#tab-(.+)/);

        if (match && tabs) {
            const slug = match[1];
            const tabIndex = tabs.findIndex((tab: any) => tab.node.slug === slug);
            if (tabIndex !== -1) {
                // Clean the URL by removing the hash
                window.history.replaceState(null, '', window.location.pathname);
                return tabIndex;
            }
        }

        const shapesTabIndex = tabs.findIndex((tab: any) => tab.node.slug === 'seats');
       
        if(shapesTabIndex !== -1) {
            return shapesTabIndex;
        }
        // Check localStorage for the previously selected tab
        const storedTab = localStorage.getItem('activeTab');
        return storedTab ? parseInt(storedTab, 10) : 0;
    };


    const [value, setValue] = useState<number>(getInitialTab);

    // Update tab value when the URL hash changes
    useEffect(() => {
        const handleHashChange = () => {
            const newTab = getInitialTab();
            setValue(newTab);
        };

        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, [tabs]);

    // Set initial tab value on component mount
    useEffect(() => {
        const initialTab = getInitialTab();
        setValue(initialTab);
    }, [tabs]);

    const handleChange = (
        event: React.SyntheticEvent<Element, Event>,
        selectedTab: number,
    ) => {
        if (tabs) {
            setValue(selectedTab);
        }
    };

    const handleSelectChange = (event: any) => {
        const selectedTab = event.target.value;
        if (tabs) {
            setValue(selectedTab);
        }
    };

    return (
        <React.Fragment>
            <Grid
                container
                sx={{
                    minHeight: '600px',
                    border: `1px solid ${theme.palette.divider}`,
                    alignItems: {
                        xs: 'flex-start',
                        sm: 'flex-start',
                        md: 'flex-start',
                    },
                    alignContent: 'flex-start',
                }}
            >
                <Grid
               
                    item
                    xs={12}
                    sm={4}
                    md={2.5}
                    paddingLeft={'0px !important'}
                    sx={{
                        backgroundColor: theme.palette.background.level2,
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Box
                        padding={1}
                       
                        sx={{
                            display: 'flex',
                            flexDirection: {
                                xs: 'row',
                                sm: 'column',
                                md: 'column',
                            },
                            justifyContent: 'space-between',
                          
                        }}
                    >
                        <Typography
                            component={'h3'}
                            fontSize={theme.spacing(2.5)}
                            fontWeight={'bold'}
                            paddingX={theme.spacing(1)}
                            paddingY={theme.spacing(1)}
                        >
                            Categories
                        </Typography>
                        {!isMobile && (
                            <Divider
                                sx={{
                                    backgroundColor: theme.palette.divider,
                                    height: '1.5px',
                                }}
                            />
                        )}

                        {isMobile ? (
                            <Select
                                value={value}
                                onChange={handleSelectChange}
                                displayEmpty
                                sx={{
                                    minWidth: '150px',
                                    textAlign: 'left',
                                    zIndex: 0,
                                    '& .MuiSelect-select': {
                                        paddingX: theme.spacing(1.5),
                                        paddingY: theme.spacing(0),
                                    },
                                }}
                            >
                                {tabs &&
                                    tabs.length > 0 &&
                                    tabs.map((category: any, index: any) => (
                                        <MenuItem
                                            key={index}
                                            value={index}
                                            {...a11yProps(index)}
                                            sx={{
                                                fontSize: '1rem',
                                                paddingX: theme.spacing(1),
                                                paddingY: 0,
                                                marginY: theme.spacing(0.5),
                                                minHeight: theme.spacing(4.5),
                                                justifyContent: 'space-between',
                                                borderRadius: theme.spacing(0.5),
                                                '& .Mui-selected': {
                                                    color: '#fff',
                                                    fontWeight: 'bold',
                                                    background: theme.palette.primary.light,
                                                },
                                            }}
                                        >
                                            {parse(category.node.name)}
                                        </MenuItem>
                                    ))}
                            </Select>
                        ) : (
                            <Tabs
                                value={value}
                                onChange={handleChange}
                                aria-label="Shapes Tab"
                                variant="scrollable"
                                orientation="vertical"
                                scrollButtons={isMd ? false : true}
                                allowScrollButtonsMobile
                                sx={{
                                    textAlign: 'left',
                                }}
                                TabIndicatorProps={{
                                    style: {
                                        display: 'none',
                                    },
                                }}
                            >
                                {tabs &&
                                    tabs.length > 0 &&
                                    tabs.map((category: any, index: any) => (
                                        <Tab
                                            key={index}
                                            label={parse(category.node.name)}
                                            {...a11yProps(index)}
                                            icon={<KeyboardArrowRightIcon />}
                                            iconPosition="end"
                                            wrapped
                                            sx={{
                                                fontSize: '1rem',
                                                paddingX: theme.spacing(1.2),
                                                paddingY: 0,
                                                marginY: theme.spacing(0.5),
                                                minHeight: theme.spacing(4.5),
                                                justifyContent: 'space-between',
                                                borderRadius: theme.spacing(0.5),
                                                '&.Mui-selected': {
                                                    color: '#fff',
                                                    fontWeight: 'bold',
                                                    background: theme.palette.primary.light,
                                                },
                                            }}
                                        />
                                    ))}
                            </Tabs>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12} sm={8} md={9.5}>
                    {tabs?.length > 0 &&
                        tabs.map((item: any, idx: any) => (
                            <CustomTabPanel key={idx} value={value} index={idx}>
                                <AccordianCategoryShapes
                                    item={item}
                                    {...item}
                                />
                            </CustomTabPanel>
                        ))}
                </Grid>
            </Grid>
        </React.Fragment>
    );
}
